import {createStore} from 'vuex';

export default createStore({
    state: {
        appVersion: '1.0',
        appShortName: 'RISR',
        appLongName: 'NIH RCDC Inclusion Statistics Report',
        availableYears: [2023,2021,2020,2019,2018],
        selectedYear: 2023,
        defaultYear: 2023,
        yearsData: {},
        topFilterPresets: null,
        singleStudiesFilter: 'All Studies',
        selectedFacet: 'Gender',
        loadingNewData: false,
        selectedRcdc: null,
        selectedRcdcData: null,
        selectedIc: '',
        selectedICData: null,
        selectedIcFilter: 'Total, NIH',
        tableColumns: [],
        exportOptions: [
            {
                name: 'Export Data as Excel',
                value: 'xls'
            },
            {
                name: 'Export Data as CSV',
                value: 'csv'
            }
        ],
        rcdcFilterValue: null,
        afterFilteringData: false,
        parsedAndFilteredData: null,
        parsedIcData: null,
        icViewFromHome: false,
        columnView: 'summary',
        appInit: false,
    },
    getters: {
        appVersion: state => state.appVersion,
        appShortName: state => state.appShortName,
        appLongName: state => state.appLongName,
        yearsData: state => state.yearsData,
        selectedYear: state => state.selectedYear,
        defaultYear: state => state.defaultYear,
        topFilterPresets: state => state.topFilterPresets,
        GenderColumns: state => state.GenderColumns,
        singleStudiesFilter: state => state.singleStudiesFilter,
        selectedFacet: state => state.selectedFacet,
        loadingNewData: state => state.loadingNewData,
        tableColumns: state => state.tableColumns,
        exportOptions: state => state.exportOptions,
        selectedTable: state => state.selectedTable,
        selectedRcdc: state => state.selectedRcdc,
        selectedIc: state => state.selectedIc,
        selectedIcFilter: state => state.selectedIcFilter,
        rcdcFilterValue: state => state.rcdcFilterValue,
        afterFilteringData: state => state.afterFilteringData,
        parsedAndFilteredData: state => state.parsedAndFilteredData,
        selectedRcdcData: state => state.selectedRcdcData,
        selectedICData: state => state.selectedICData,
        icViewFromHome: state => state.icViewFromHome,
        columnView: state => state.columnView,
        appInit: state => state.appInit
    },
    mutations: {
        setYearsData (state, payload) {
            state.yearsData[payload.year] = payload.data;
        },
        setSelectedYear (state, payload) {
            state.selectedYear = payload;
        },
        setTopFilterData (state, payload) {
            state.topFilterPresets = payload;
        },
        setSingleStudiesFilter (state, payload) {
            state.singleStudiesFilter = payload;
        },
        setSelectedFacet (state, payload) {
            state.selectedFacet = payload;
        },
        setLoadingNewData (state, payload) {
            state.loadingNewData = payload;
        },
        setTableColumns (state, payload) {
            for (let index = 0; index < payload.data.length; index++) {
                const column = payload.data[index];

                switch (column.dataField) {
                    case 'rcdcCategory':
                        payload.data[index].display = (payload.route.name === 'index' || payload.route.name === 'home');
                        break;
                    case 'ic':
                        payload.data[index].display = payload.route.name === 'rcdc';
                        break;
                }
            }

            state.tableColumns = payload.data;
        },
        setSelectedTable (state, payload) {
            state.selectedTable = payload;
        },
        setSelectedRcdc (state, payload) {
            state.selectedRcdc = payload;
        },
        setSelectedIc (state, payload) {
            state.selectedIc = payload;
        },
        setSelectedIcFilter (state, payload) {
            state.selectedIcFilter = payload;
        },
        setRcdcFilterValue (state, payload) {
            state.rcdcFilterValue = payload;
        },
        setAfterFilteringData (state, payload) {
            state.afterFilteringData = payload;
        },
        setSelectedRcdcData (state, payload) {
            state.selectedRcdcData = payload;
        },
        parseAndFilterRawData (state) {
            var results = [],
                tempItem;

            for (let index = 0; index < state.yearsData[state.selectedYear].length; index++) {
                const dataRow = state.yearsData[state.selectedYear][index];

                tempItem = dataRow.icData.filter((x) => {
                    return x['studyPopulation'] === state.singleStudiesFilter && x.ic === state.selectedIcFilter;
                })[0];

                if (tempItem) {
                    results.push(tempItem);
                }
            }

            state.parsedAndFilteredData = results;
        },
        getRCDCData (state) {
            state.selectedRcdcData = _(state.yearsData[state.selectedYear])
                .filter(x => x['rcdcCategory'] === state.selectedRcdc)
                .value()[0].icData
                .filter(x => x['studyPopulation'] === state.singleStudiesFilter);
        },
        getICData (state) {
            state.selectedICData = _(state.yearsData[state.selectedYear])
                .filter(x => x['rcdcCategory'] === state.selectedRcdc)
                .value()[0].icData
                .filter(x => x['ic'] === state.selectedIc);
        },
        setIcViewFromHome (state, payload) {
            state.icViewFromHome = payload;
        },
        resetTopFilters (state) {
            // state.selectedYear = state.defaultYear;
            state.selectedIc = '';
            state.selectedIcFilter = 'Total, NIH';
            state.selectedFacet = 'Gender';
            state.singleStudiesFilter = 'All Studies';
            state.rcdcFilterValue = null;
        },
        setColumnView (state, payload) {
            state.columnView = payload;
        },
        setAppInit (state, payload) {
            state.appInit = payload;
        }
    },
    actions: {
        setYearsData ({ commit }, payload) {
            commit('setYearsData', payload);
        },
        setSelectedYear ({ commit }, payload) {
            commit('setSelectedYear', payload);
        },
        setTopFilterData ({ commit }, payload) {
            commit('setTopFilterData', payload);
        },
        setSingleStudiesFilter ({ commit }, payload) {
            commit('setSingleStudiesFilter', payload);
        },
        setSelectedFacet ({ commit }, payload) {
            commit('setSelectedFacet', payload);
        },
        setLoadingNewData ({ commit }, payload) {
            commit('setLoadingNewData', payload);
        },
        setTableColumns ({ commit }, payload) {
            commit('setTableColumns', payload);
        },
        setSelectedTable ({ commit }, payload) {
            commit('setSelectedTable', payload);
        },
        setSelectedRcdc ({ commit }, payload) {
            commit('setSelectedRcdc', payload);
        },
        setSelectedIc ({ commit }, payload) {
            commit('setSelectedIc', payload);
        },
        setSelectedIcFilter ({ commit }, payload) {
            commit('setSelectedIcFilter', payload);
        },
        setRcdcFilterValue ({ commit }, payload) {
            commit('setRcdcFilterValue', payload);
        },
        setAfterFilteringData ({ commit }, payload) {
            commit('setAfterFilteringData', payload);
        },
        setSelectedRcdcData ({ commit }, payload) {
            commit('setSelectedRcdcData', payload);
        },
        parseAndFilterRawData ({ commit }, payload) {
            commit('parseAndFilterRawData', payload);
        },
        setIcViewFromHome ({ commit }, payload) {
            commit('setIcViewFromHome', payload);
        },
        resetTopFilters ({ commit }, payload) {
            commit('resetTopFilters', payload);
        },
        getRCDCData ({ commit }, payload) {
            commit('getRCDCData', payload);
        },
        getICData ({ commit }, payload) {
            commit('getICData', payload);
        },
        setColumnView ({ commit }, payload) {
            commit('setColumnView', payload);
        },
        setAppInit ({ commit }, payload) {
            commit('setAppInit', payload);
        },
    }
});